/**
 * useReportCopilotBridge — the chat↔form bridge the Report Studio hands to its
 * floating CopilotDock (the report twin of useMinutesCopilotBridge):
 *
 *   chat → form : update_page_config { target: 'report' } tool calls arrive as
 *                 patch_page_config client actions; this hook maps them onto
 *                 ReportConfigContext (fields flash teal for ~2.4s; a toast
 *                 lists the changes when the inspector is closed).
 *   form → chat : getPageContext() snapshots the live form + document state,
 *                 sent with every dock turn (Copilot-style ambient grounding).
 */
import { useRef, useState } from 'react';
import { useUI } from '../../context/UIContext';
import { useReportConfig } from '../../context/ReportConfigContext';
import { buildDocPair } from '../../chat/pageDoc';
import { CARO_CLAUSE_IDS } from '../../data/reports/auditorReport/caro';

// update_page_config report fields ↔ ReportConfigContext keys are 1:1 (the
// backend whitelist was written against this config shape).
const SCALAR_KEYS = new Set([
  'companyType', 'fyEnded', 'framework', 'opinionType', 'qualificationBasis',
  'firmName', 'frn', 'partnerName', 'membershipNo', 'udin', 'place', 'reportDate',
]);
const BOOL_KEYS = new Set(['includeKAM', 'includeEmphasis', 'includeOtherInfo']);

// Enum fields feed <select>s — a near-miss casing from the model ("indas",
// "Qualified") would otherwise leave the control blank. Normalize; a value
// that matches nothing is DROPPED, never guessed.
const normalizeEnum = (key, v) => {
  const s = String(v).trim();
  if (key === 'framework') return /ind/i.test(s) ? 'IndAS' : /^as$/i.test(s) ? 'AS' : null;
  if (key === 'opinionType') {
    const o = s.toLowerCase();
    return ['unmodified', 'qualified', 'adverse', 'disclaimer'].includes(o) ? o : null;
  }
  if (key === 'qualificationBasis') {
    const o = s.toLowerCase();
    if (o.includes('scope') || o.includes('limitation') || o.includes('evidence') || o.includes('possible')) return 'scope_limitation';
    if (o.includes('misstate')) return 'misstatement';
    return null;
  }
  if (key === 'companyType') {
    const o = s.toLowerCase();
    if (['pvt', 'private'].some((t) => o.includes(t))) return 'pvt';
    if (o.includes('unlisted') || (o.includes('public') && !o.includes('listed'))) return 'public';
    if (o.includes('listed')) return 'listed';
    if (o.includes('public')) return 'public';
    if (['small', 'opc'].some((t) => o.includes(t))) return 'small';
    return null;
  }
  return s;
};

// The report's narrative slots. `disputedDues` is the one free-text slot inside
// ANNEXURE A (CARO clause 3(vii)(b)) — wiring it here is what stops Vee editing
// the main report itself and telling the user to fix the annexure by hand.
// Key Audit Matters are structured (an array of objects), so they stay with the
// full draft path rather than a prose patch.
const AI_SECTION_KEYS = new Set(['basisModified', 'emphasis', 'disputedDues']);
const AI_SECTION_LABELS = {
  basisModified: 'Basis for Modified Opinion',
  emphasis: 'Emphasis of Matter',
  disputedDues: 'disputed statutory dues (Annexure A)',
};

// Annexure A clause comments Vee may rewrite. The backend validates the ids it
// accepts against its own mirror of this list; re-checking here means a client
// action that somehow carried a bad id can never write junk into the config the
// renderer reads.
const CLAUSE_IDS = new Set(CARO_CLAUSE_IDS);

export function useReportCopilotBridge({
  getDocState, isInspectorOpen, applyAiSections, applyCaroClauses,
}) {
  const { showToast } = useUI();
  const { config, updateConfig } = useReportConfig();

  const [flashKeys, setFlashKeys] = useState(null);
  const flashTimer = useRef(null);

  const flash = (keys) => {
    setFlashKeys(new Set(keys));
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashKeys(null), 2400);
  };

  const applyPatch = (patch) => {
    const touched = [];
    for (const [k, v] of Object.entries(patch || {})) {
      if (v === undefined) continue;
      if (SCALAR_KEYS.has(k)) {
        const val = normalizeEnum(k, v);
        if (val === null) continue; // unrecognized enum value — drop, never guess
        updateConfig(k, val);
        touched.push(k);
      } else if (BOOL_KEYS.has(k) && typeof v === 'boolean') {
        updateConfig(k, v);
        touched.push(k);
      }
    }
    if (touched.length) {
      flash(touched);
      // Inspector closed → the flash is invisible; say what changed instead.
      if (!isInspectorOpen()) showToast(`Vee filled in: ${[...new Set(touched)].join(', ')}`, 'info');
    }
    return touched;
  };

  // Narrative sections → the deterministic rebuild (main report AND annexures).
  const applySections = (sections) => {
    // A key that is PRESENT with an empty value is an explicit clear —
    // dropping it here made "remove the disputed dues note" a silent no-op
    // while the tool result still claimed success.
    const clean = {};
    for (const [k, v] of Object.entries(sections || {})) {
      if (AI_SECTION_KEYS.has(k) && v != null) clean[k] = String(v).trim();
    }
    const keys = Object.keys(clean);
    if (!keys.length || typeof applyAiSections !== 'function') return [];
    applyAiSections(clean);
    const cleared = keys.filter((k) => !clean[k]);
    const rewrote = keys.filter((k) => clean[k]);
    const label = (k) => AI_SECTION_LABELS[k] || k;
    if (rewrote.length) showToast(`Vee rewrote: ${rewrote.map(label).join(', ')}`, 'info');
    if (cleared.length) showToast(`Vee cleared: ${cleared.map(label).join(', ')}`, 'info');
    return keys;
  };

  // Per-clause CARO comment overrides → config.caroClauses, MERGED over what
  // the config already holds so patching clause (i)(a) can't wipe the comment
  // the user set on clause (vii)(b). An id present with an empty value is an
  // explicit revert to the template wording, so the key is DELETED (leaving ''
  // in the map would keep a dead entry in the studio's override list forever).
  const applyClauses = (incoming) => {
    if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) return [];
    const rewrote = [];
    const cleared = [];
    const next = { ...(config.caroClauses || {}) };
    for (const [id, value] of Object.entries(incoming)) {
      if (!CLAUSE_IDS.has(id)) continue; // unknown id — drop, never guess a clause
      const text = String(value == null ? '' : value).trim();
      if (text) { next[id] = text; rewrote.push(id); } else if (next[id] != null) { delete next[id]; cleared.push(id); }
    }
    if (!rewrote.length && !cleared.length) return [];
    if (typeof applyCaroClauses === 'function') applyCaroClauses(next);
    else updateConfig('caroClauses', next);
    if (rewrote.length) showToast(`Vee rewrote Annexure A clause${rewrote.length > 1 ? 's' : ''} ${rewrote.join(', ')}`, 'info');
    if (cleared.length) showToast(`Vee restored the standard wording for clause${cleared.length > 1 ? 's' : ''} ${cleared.join(', ')}`, 'info');
    return [...rewrote, ...cleared];
  };

  const handleClientActions = (actions) => {
    for (const a of actions || []) {
      if (a.type === 'patch_page_config' && a.target === 'report') {
        applyPatch(a.patch || {});
        applySections(a.aiSections);
        applyClauses(a.caroClauses);
      }
      // navigate / switch_company directives are ignored while the studio is open.
    }
  };

  const getPageContext = () => {
    const doc = getDocState(); // { docSource, hasUserEdits, currentHtml, baselineHtml }
    return {
      page: 'report',
      // The document itself — main report AND every enabled annexure, joined by
      // combinedHtml. Without this the Report dock sent Vee a form snapshot and
      // no text, so the annexures were literally invisible to it (the Minutes
      // dock has always sent the pair; see chat/pageDoc.js for why two versions).
      doc: buildDocPair(doc),
      config: {
        companyType: config.companyType,
        companyName: config.companyName,
        cin: config.cin,
        fyEnded: config.fyEnded,
        framework: config.framework,
        opinionType: config.opinionType,
        qualificationBasis: config.qualificationBasis,
        includeKAM: !!config.includeKAM,
        includeOtherInfo: !!config.includeOtherInfo,
        includeEmphasis: !!config.includeEmphasis,
        firmName: config.firmName,
        frn: config.frn,
        partnerName: config.partnerName,
        membershipNo: config.membershipNo,
        udin: config.udin,
        place: config.place,
        reportDate: config.reportDate,
        // Clause ids the auditor has already restated on Annexure A. Ids only —
        // the comment text is in the document block Vee already receives, and
        // repeating it would double the prompt for no gain. Omitted when empty:
        // the prompt turns every '' here into a "still missing" field, and a
        // report with no restated clauses is not missing anything.
        ...(Object.keys(config.caroClauses || {}).length
          ? { caroClausesOverridden: Object.keys(config.caroClauses).join(', ') }
          : {}),
      },
      docSource: doc.docSource,
      hasUserEdits: doc.hasUserEdits,
    };
  };

  return { flashKeys, handleClientActions, getPageContext };
}

export default useReportCopilotBridge;
